import { z } from 'zod'
import { ArchivableSchema } from '../archive'
import { AreaJobInfoSchema } from '../areaJob/info'
import { AuditPropsSchema } from '../common'
import { CustomFieldSearchResultSchema } from '../customField'
import { CustomFieldFilterSchema } from '../customFieldMap'
import { AccountStatusIdSchema, TeamIdSchema, UserIdSchema, WorkflowStageIdSchema, idSchemaV1, isId } from '../ids'
import { WithImportIdSchema } from '../import/model'
import { LatLngSchema } from '../locations/latLng'
import { UTCMillisecondsSchema } from '../time'

export const AreaIdSchema = idSchemaV1('Area')
export type AreaId = z.infer<typeof AreaIdSchema>
export const isAreaId = isId(AreaIdSchema)

export const ASSIGNABLE_AREA_TYPES = ['user', 'team'] as const
export const AreaAssignmentTypeSchema = z.enum([...ASSIGNABLE_AREA_TYPES, 'company', 'draft'])
export type AreaAssignmentType = z.infer<typeof AreaAssignmentTypeSchema>

export const AREA_STATE_NAMES = ['draft', 'scheduled', 'assigned', 'expiring', 'unassigned'] as const
export const AreaStateNameSchema = z.enum(AREA_STATE_NAMES)
export type AreaStateName = z.infer<typeof AreaStateNameSchema>

export const AreaFiltersSchema = z.object({
  cities: z.array(z.string()).optional(),
  states: z.array(z.string()).optional(),
  zipCodes: z.array(z.string()).optional(),
  statusIds: z.array(AccountStatusIdSchema).optional(),
  workflowStageIds: z.array(WorkflowStageIdSchema).optional(),
  teamIds: z.array(TeamIdSchema).optional(),
  userIds: z.array(UserIdSchema).optional(),
  customFields: z.array(CustomFieldSearchResultSchema).optional(),
  advancedCustomFields: CustomFieldFilterSchema.optional(),
})
export type AreaFilters = z.infer<typeof AreaFiltersSchema>

export const AreaAssignmentConfigSchema = z.discriminatedUnion('enabled', [
  z.object({ enabled: z.literal(false), autoRunOnImport: z.boolean().optional() }),
  z.object({ enabled: z.literal(true), autoRunOnImport: z.boolean().optional(), order: z.number() }),
])
export type AreaAssignmentConfig = z.infer<typeof AreaAssignmentConfigSchema>

export const AreaRunTypeSchema = z.enum(['assign', 'unassign', 'reassign'])
export type AreaRunType = z.infer<typeof AreaRunTypeSchema>

export const AreaRunHistoryEntrySchema = z.object({
  timestamp: UTCMillisecondsSchema,
  count: z.number(),
  type: AreaRunTypeSchema.optional(),
  runBy: UserIdSchema.optional(),
  scheduled: z.boolean().optional(),
  process: z.string().optional(),
  teamId: TeamIdSchema.optional(),
  userIds: z.array(UserIdSchema).optional(),
})
export type AreaRunHistoryEntry = z.infer<typeof AreaRunHistoryEntrySchema>

const AreaScheduleEntryBaseSchema = z.object({ at: UTCMillisecondsSchema, by: UserIdSchema.optional() })
export const AreaScheduleEntrySchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('assign'), ...AreaScheduleEntryBaseSchema.shape }),
  z.object({ type: z.literal('unassign'), ...AreaScheduleEntryBaseSchema.shape }),
  z.object({
    type: z.literal('reassign'),
    teamId: TeamIdSchema.optional(),
    userIds: z.array(UserIdSchema).optional(),
    ...AreaScheduleEntryBaseSchema.shape,
  }),
])
export type AreaScheduleEntry = z.infer<typeof AreaScheduleEntrySchema>

export const UnsavedAreaSchema = z.object({
  name: z.string().optional(),
  coordinates: z.array(LatLngSchema).default([]),
  assignmentType: AreaAssignmentTypeSchema,
  teamId: TeamIdSchema.optional(),
  userIds: z.array(UserIdSchema).optional(),
  color: z.string().optional(),
  note: z.string().optional(),
  filters: AreaFiltersSchema.optional(),
  assignmentConfig: AreaAssignmentConfigSchema.optional(),
  schedule: z.array(AreaScheduleEntrySchema).optional(),
})
export type UnsavedArea = z.infer<typeof UnsavedAreaSchema>

export const AreaAccountCountsSchema = z.object({
  total: z.number().optional(),
  assigned: z.number().optional(),
  unassigned: z.number().optional(),
})
export type AreaAccountCounts = z.infer<typeof AreaAccountCountsSchema>

export const AreaDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...ArchivableSchema.shape,
  ...UnsavedAreaSchema.shape,
  ...WithImportIdSchema.shape,
  areaId: AreaIdSchema,
  deleteOnCompletion: z.boolean().optional(),
  runHistory: z.array(AreaRunHistoryEntrySchema).max(20).optional(),
  jobInfo: AreaJobInfoSchema.optional(),
  accountCounts: AreaAccountCountsSchema.optional(),
})
export type AreaData = z.infer<typeof AreaDataSchema>
